"use client";

import React from "react";
import { MapPin, Map, CircleCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const cities = ["Burlington", "Greensboro", "High Point", "Mebane", "Graham"];
const counties = [];
export default function ServiceArea() {
  const {
    ref: sectionRef,
    isVisible: isSectionVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: leftRef,
    isVisible: isLeftVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const {
    ref: rightRef,
    isVisible: isRightVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <section id="service-area" ref={sectionRef} className="w-full bg-background py-16 md:py-24 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div className={`grid grid-cols-1 lg:grid-cols-2 rounded-3xl overflow-hidden shadow-2xl border border-border transition-all duration-1000 ease-out ${isSectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {}
          <div ref={leftRef} className={`relative bg-muted/30 p-8 md:p-12 flex items-center justify-center min-h-[400px] lg:min-h-[600px] transition-all duration-1000 delay-200 ease-out ${isLeftVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,var(--color-primary)_0%,transparent_70%)] opacity-5 pointer-events-none" />
            
            <div className="w-full max-w-md relative z-10">
              <svg viewBox="0 0 500 500" className="w-full h-auto drop-shadow-xl" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
                    <feGaussianBlur stdDeviation="8" result="blur" />
                    <feComposite in="SourceGraphic" in2="blur" operator="over" />
                  </filter>
                </defs>

                {}
                <g className="stroke-background stroke-[3px] transition-all duration-300">
                  {}
                  <path d="M120,150 C180,130 240,140 260,160 C280,220 270,280 250,320 C190,340 140,310 110,260 C90,210 100,170 120,150 Z" className="fill-muted hover:fill-primary/20 transition-colors duration-300 cursor-pointer" />
                  {}
                  <path d="M260,160 C320,140 380,150 410,190 C430,240 410,300 370,330 C320,350 270,340 250,320 C270,280 280,220 260,160 Z" className="fill-muted hover:fill-primary/20 transition-colors duration-300 cursor-pointer" />
                  {}
                  <path d="M110,260 C140,310 190,340 250,320 C270,340 320,350 340,400 C300,450 220,460 160,430 C120,390 90,320 110,260 Z" className="fill-muted hover:fill-primary/20 transition-colors duration-300 cursor-pointer" />
                </g>

                {}
                <g className="pointer-events-none">
                  {}
                  <g transform="translate(180, 210)">
                    <circle cx="0" cy="0" r="8" className="fill-primary" filter="url(#glow)" />
                    <circle cx="0" cy="0" r="4" className="fill-background" />
                    <text x="0" y="-15" className="text-sm font-semibold fill-foreground" textAnchor="middle">Greensboro</text>
                  </g>
                  
                  {}
                  <g transform="translate(150, 270)">
                    <circle cx="0" cy="0" r="6" className="fill-primary" />
                    <text x="-10" y="5" className="text-xs font-medium fill-foreground" textAnchor="end">High Point</text>
                  </g>

                  {}
                  <g transform="translate(320, 220)">
                    <circle cx="0" cy="0" r="8" className="fill-primary" filter="url(#glow)" />
                    <circle cx="0" cy="0" r="4" className="fill-background" />
                    <text x="0" y="-15" className="text-sm font-semibold fill-foreground" textAnchor="middle">Burlington</text>
                  </g>

                  {}
                  <g transform="translate(380, 200)">
                    <circle cx="0" cy="0" r="6" className="fill-primary" />
                    <text x="10" y="5" className="text-xs font-medium fill-foreground" textAnchor="start">Mebane</text>
                  </g>

                  {}
                  <g transform="translate(330, 260)">
                    <circle cx="0" cy="0" r="6" className="fill-primary" />
                    <text x="10" y="5" className="text-xs font-medium fill-foreground" textAnchor="start">Graham</text>
                  </g>
                </g>
              </svg>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`bg-secondary p-8 md:p-12 lg:p-16 flex flex-col justify-center transition-all duration-1000 delay-400 ease-out ${isRightVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-[var(--accent-cream)]/10 text-[var(--accent-cream)] w-fit mb-6 border border-[var(--accent-cream)]/20">
              <MapPin className="size-4" />
              <span className="text-sm font-medium tracking-wide uppercase">Service Area</span>
            </div>
            
            <h2 className="text-[var(--accent-cream)] font-default text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
              Serving Your Local Community
            </h2>
            
            <p className="text-[var(--accent-cream)]/80 text-lg mb-10 max-w-xl leading-relaxed">
              We are proud to provide our professional services across the Piedmont Triad region. Our team is dedicated to delivering exceptional results to homes and businesses in your neighborhood.
            </p>

            <div className="mb-10">
              
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {cities.map((city, i) => <React.Fragment key={`${city}-${i}`}>
                    <li data-index={i} className="flex items-center gap-3 text-[var(--accent-cream)]/90 group">
                      <div className="size-2 rounded-lg bg-primary/50 group-hover:bg-primary transition-colors duration-300" />
                      <span className="font-medium">{city}</span>
                    </li>
                    {i === 3 && <li data-index={i} className="flex items-center gap-3 text-[var(--accent-cream)]/90 group">
                        <div className="size-2 rounded-lg bg-primary/50 group-hover:bg-primary transition-colors duration-300" />
                        <span className="font-medium">All of North Carolina</span>
                      </li>}
                  </React.Fragment>)}
              </ul>
            </div>

            <Separator className="bg-[var(--accent-cream)]/20 mb-10" />

            <div>
              
              <div className="flex flex-wrap gap-3">
                {counties.map((county, i) => <Badge key={county} data-index={i} variant="outline" className="border-[var(--accent-cream)]/30 text-[var(--accent-cream)] bg-transparent hover:bg-[var(--accent-cream)]/10 hover:border-[var(--accent-cream)]/50 transition-all duration-300 px-4 py-1.5 text-sm">
                    {county}
                  </Badge>)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}